import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import type { Deficit } from './lib/packageFit'
import { isLivePhase } from './lib/paymentPhase'
import { usePaymentWatcher, type PaymentWatch } from './usePaymentWatcher'
import { useCancelPaymentMutation } from './paymentsApi'
import type { PaymentMethod } from './lib/paymentMethods'
// `TopUpDialog` здесь НЕ импортируется: он читает этот контекст, и взаимный
// импорт двух модулей — лишний цикл. Диалог монтирует `App.tsx` внутри
// провайдера.

/**
 * Ожидаемый платёж. Ссылка на воронку хранится ВМЕСТЕ с транзакцией, а не
 * рядом: `window.open` зовётся уже после `await buy(...)`, то есть вне
 * синхронного жеста автора, и Safari такой попап штатно блокирует — без
 * сохранённой ссылки автор остался бы в ожидании без единого способа открыть
 * оплату. Одно значение вместо двух состояний: транзакция без ссылки и ссылка
 * без транзакции — оба невалидны, и собирать их порознь незачем.
 */
export interface PendingPayment {
  transactionId: number
  paymentUrl: string | null
  /**
   * Чем платят. `null` — неизвестно: вкладка вернулась из воронки
   * (`resumePayment`), и способа она не знает.
   */
  method: PaymentMethod | null
}

/**
 * ЧТО показывает диалог. `credits` — окно кредитов (баланс, заморозка, история
 * движений); с него начинается открытие «просто посмотреть», открытие из отказа
 * прогона его минует: автору там нужны деньги, а не чтение.
 *
 * Отдельно от транзакции намеренно: пока шаг выводился
 * из одного лишь наличия `payment`, брошенная оплата запирала диалог навсегда
 * — вернуться к витрине было нечем, а транзакция не снималась, потому что за
 * ней ещё могли прийти деньги. Разведя «за чем наблюдаем» и «что на экране»,
 * получаем и путь назад, и продолжение наблюдения (тост об успехе при закрытом
 * диалоге).
 */
type TopUpStep = 'credits' | 'packages' | 'payment'

interface CreditsDialogValue {
  isOpen: boolean
  deficit: Deficit | null
  open: (deficit?: Deficit | null, target?: TopUpTarget) => void
  close: () => void
  payment: PendingPayment | null
  /** Транзакция заведена — диалог сразу переходит на экран ожидания. */
  setPayment: (payment: PendingPayment | null) => void
  step: TopUpStep
  /** Назад к витрине, НЕ трогая наблюдение за платежом. */
  showPackages: () => void
  /**
   * Автор бросает оплату и возвращается к витрине. Живую транзакцию снимаем
   * (best-effort: ошибку глушим, ответа не ждём), за терминальной снимать
   * нечего. Наблюдение кончается: следующее открытие — витрина, а не ожидание.
   */
  abandonPayment: () => void
  /** Назад в окно кредитов. */
  showCredits: () => void
  watch: PaymentWatch
  /**
   * Вкладка вернулась из воронки (`PaymentReturn`): открыть диалог сразу на
   * ожидании этой транзакции.
   */
  resumePayment: (transactionId: number) => void
  /**
   * Диалог стоит на той транзакции, с которой вкладка вернулась из воронки, —
   * «Вернуться к работе» закрывает вкладку. Новая покупка в этой же вкладке
   * флаг гасит: в исходной её итога нет.
   */
  isReturnTab: boolean
}

/**
 * Куда открывать. Значение по умолчанию выводится из дефицита, и явно его
 * передаёт ровно одна точка входа — пункт меню «Пополнить» в профиле, которой
 * нужна витрина без всякого отказа.
 */
export type TopUpTarget = 'credits' | 'packages'

const CreditsDialogContext = createContext<CreditsDialogValue | null>(null)

/**
 * Диалог пополнения глобальный: точек входа пять (блок баланса в профиле и
 * четыре платных старта), а достижимость у него, в отличие от «повторить» /
 * «показать прогон» / «задать стиль», не зависит от экрана — поэтому кнопку
 * рисует сам `FailureBanner`, а не пять родителей пропом.
 *
 * Владельцем ожидания оплаты тоже назначен провайдер: закрытый диалог не
 * должен убивать транзакцию, открытую в соседней вкладке.
 */
export const CreditsDialogProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [deficit, setDeficit] = useState<Deficit | null>(null)
  const [payment, setPaymentState] = useState<PendingPayment | null>(null)
  const [step, setStep] = useState<TopUpStep>('credits')
  const [returnTransactionId, setReturnTransactionId] = useState<number | null>(null)
  const [cancelPayment] = useCancelPaymentMutation()
  // Второй аргумент — «автор и так увидит исход сам», а не «диалог открыт»:
  // экран успеха живёт ТОЛЬКО на шаге ожидания. Уйдя с него к витрине (и
  // оставаясь в диалоге), автор без этого условия не получал ни тоста, ни
  // экрана — оплата проходила полностью молча, и второй пакет покупался
  // вслепую. Проверено живьём на проде 2026-08-14.
  const watch = usePaymentWatcher(payment?.transactionId ?? null, isOpen && step === 'payment')
  const isLive = payment !== null && isLivePhase(watch.phase)
  const isReturnTab = payment !== null && payment.transactionId === returnTransactionId

  const value = useMemo<CreditsDialogValue>(
    () => ({
      isOpen,
      deficit,
      open: (next?: Deficit | null, target?: TopUpTarget) => {
        setDeficit(next ?? null)
        // Транзакция, за которой уже нечего наблюдать (оплачена, отменена,
        // отказана), не должна встречать автора старым итогом вместо витрины —
        // ни после «Готово», ни после терминального статуса, пришедшего при
        // закрытом диалоге. Живую (ожидание/ручной режим) сохраняем вместе с
        // тем шагом, на котором её оставили.
        if (!isLive) {
          setPaymentState(null)
          // Дефицит означает «прогон не запустился из-за денег» — читать
          // историю автору сейчас незачем, ему нужна витрина.
          setStep(target ?? (next ? 'packages' : 'credits'))
        }
        setIsOpen(true)
      },
      close: () => setIsOpen(false),
      payment,
      setPayment: (next: PendingPayment | null) => {
        setPaymentState(next)
        setStep(next ? 'payment' : 'packages')
      },
      step,
      showPackages: () => setStep('packages'),
      abandonPayment: () => {
        // Отмена не обещает, что деньги не придут: инвойс Heleket у провайдера
        // не отменить, и поздняя оплата всё равно начислит кредиты (бэк это
        // гарантирует, `complete_purchase`). Отмена лишь убирает висящую строку.
        if (payment !== null && isLive)
          void cancelPayment(payment.transactionId)
            .unwrap()
            .catch(() => {})
        setPaymentState(null)
        setStep('packages')
      },
      showCredits: () => setStep('credits'),
      watch,
      resumePayment: (transactionId: number) => {
        // Отдельно от `open`: тот снимает «неживую» транзакцию, а при входе по
        // ссылке из воронки статуса ещё нет — `open` стёр бы её до первого
        // опроса. Ссылки на воронку нет: эта вкладка сама из неё и вернулась.
        setPaymentState({ transactionId, paymentUrl: null, method: null })
        setDeficit(null)
        setStep('payment')
        setReturnTransactionId(transactionId)
        setIsOpen(true)
      },
      isReturnTab,
    }),
    [isOpen, deficit, payment, step, watch, isLive, isReturnTab, cancelPayment],
  )

  return <CreditsDialogContext.Provider value={value}>{children}</CreditsDialogContext.Provider>
}

/**
 * Точкам входа нужен только способ открыть диалог.
 *
 * Хук возвращает открывалку и БЕЗ провайдера — падает не рендер, а клик.
 * Причина: `FailureBanner` рисуется в десятке уже написанных тестов, которые
 * провайдера не поднимают, и падение на рендере превратило бы включение одной
 * кнопки в веерную правку чужих тестов. Молчаливой заглушки при этом нет:
 * тихий no-op однажды уже врал гейту черновика и в тестах, и в проде
 * (CLAUDE.md, `BlockDraftProvider`), поэтому дефолт именно бросает.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useTopUp = (): {
  open: (deficit?: Deficit | null, target?: TopUpTarget) => void
} => {
  const value = useContext(CreditsDialogContext)
  return {
    open: (deficit?: Deficit | null, target?: TopUpTarget) => {
      if (!value) throw new Error('CreditsDialogProvider не смонтирован над этим деревом')
      value.open(deficit ?? null, target)
    },
  }
}

/** Самому диалогу нужно всё состояние — он живёт только внутри провайдера. */
// eslint-disable-next-line react-refresh/only-export-components
export const useCreditsDialog = (): CreditsDialogValue => {
  const value = useContext(CreditsDialogContext)
  if (!value) throw new Error('useCreditsDialog: нет CreditsDialogProvider над деревом')
  return value
}

/** `PaymentReturn` нужен ровно один метод — возобновить ожидание по ссылке. */
// eslint-disable-next-line react-refresh/only-export-components
export const useResumePayment = (): ((transactionId: number) => void) =>
  useCreditsDialog().resumePayment
